import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Input } from '@/shared/ui'
import { useCreateInvoice } from '@/shared/hooks'

const MIN_AMOUNT = 10
const MAX_AMOUNT = 1_000_000

export interface InvoiceEntityOption {
  legalEntityId: number
  name: string
}

interface Props {
  isOpen: boolean
  /** Юрлицо задано карточкой — выбор компании не показываем. */
  entityId?: number | undefined
  entityName?: string | undefined
  /** Список для выбора компании, когда модалка открывается из общего раздела. */
  entities?: InvoiceEntityOption[]
  onClose: () => void
}

/** Счёт на пополнение демо-баланса: оплата появится только после подтверждения. */
export const InvoiceCreateModal: React.FC<Props> = ({
  isOpen,
  entityId: fixedEntityId,
  entityName,
  entities = [],
  onClose,
}) => {
  const createInvoice = useCreateInvoice()
  const [pickedEntityId, setPickedEntityId] = useState<number | ''>('')
  const [amount, setAmount] = useState('')
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setPickedEntityId(fixedEntityId ?? '')
      setAmount('')
      setComment('')
      setError(null)
    }
  }, [isOpen, fixedEntityId])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const targetEntityId = fixedEntityId ?? pickedEntityId
    if (!targetEntityId) {
      setError('Выберите юридическое лицо')
      return
    }
    const normalized = amount.trim().replace(',', '.')
    const parsed = Number(normalized)
    if (!normalized || Number.isNaN(parsed)) {
      setError('Укажите сумму счёта')
      return
    }
    if (parsed < MIN_AMOUNT || parsed > MAX_AMOUNT) {
      setError('Сумма счёта — от 10 до 1 000 000 ₽')
      return
    }

    setError(null)
    try {
      await createInvoice.mutateAsync({
        entityId: targetEntityId,
        body: {
          amount: normalized,
          ...(comment.trim() ? { comment: comment.trim() } : {}),
        },
      })
      onClose()
    } catch {
      // Ошибку уже показал interceptor API — модалка остаётся открытой.
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Создать счёт</h2>
            {entityName && <p className="mt-1 text-sm text-gray-500">{entityName}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!fixedEntityId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Юридическое лицо
              </label>
              <select
                value={pickedEntityId}
                onChange={(e) =>
                  setPickedEntityId(e.target.value ? Number(e.target.value) : '')
                }
                className="w-full border border-gray-300 rounded-lg p-2.5 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              >
                <option value="">— выберите компанию —</option>
                {entities.map((item) => (
                  <option key={item.legalEntityId} value={item.legalEntityId}>
                    {item.name}
                  </option>
                ))}
              </select>
              {entities.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  Нет доступных юрлиц: нужен доступ к разделу «Юридические лица».
                </p>
              )}
            </div>
          )}
          <Input
            label="Сумма, ₽"
            inputMode="decimal"
            placeholder="9500,00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={error || undefined}
            helperText="От 10 до 1 000 000 ₽. Баланс пополнится после подтверждения оплаты."
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Комментарий
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="Назначение счёта..."
              className="w-full border border-gray-300 rounded-lg p-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent resize-none"
            />
            <div className="text-right text-xs text-gray-400 mt-1">
              {comment.length}/500
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={createInvoice.isPending}
            >
              Отмена
            </Button>
            <Button type="submit" isLoading={createInvoice.isPending}>
              Создать счёт
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
